
<template>
  <div class="tw:!my-10" v-if="!hidden">
    <Suspense>
      <Transition :name="sector.animation" mode="out-in">
        <KeepAlive :include="['FlightSelectionPartial', 'PassengerSectionPartial']">
          <FlightSelectionPartial
            v-if="sector.is('flight')"
            :ref="ref => bind('flight', ref)"
            @pre-select="() => execute('flight', flight => flight.goToFistSection())"
            @select="(departure, returning) => {
              form.flight = { departure, returning }
              sector.next()
              execute('passenger', passenger => passenger.$el.scrollIntoView({ behavior: 'smooth', block: 'center' }))
            }"
          />
          <PassengerSectionPartial
            v-else-if="sector.is('passenger')"
            :ref="ref => bind('passenger', ref)"
            :pax="passengers"
            :duration="form.duration"
            @back="() => {
              sector.prev()
              execute('flight', flight => flight.goToFistSection())
            }"
            @submit="(passengers, contact, duration) => {
              form.preferred = { duration }
              form.passengers = passengers
              form.contact = contact
              create()
            }"
            :loading="creating"
          />
          <PaymentSectionPartial
            v-else-if="sector.is('payment')"
            :invoice="invoice!._id"
            :departure-flight-info="form.flight?.departure"
            :return-flight-info="form.flight?.returning"
            @back="sector.prev()"
            @success="obtain"
          />
          <ObtainTicketPartial 
            v-else-if="sector.is('obtain')"
            :invoice="invoice!._id"
          />
        </KeepAlive>
      </Transition>
      <template #fallback>
        Loading...
      </template>
    </Suspense>
  </div>
</template>

<script lang="ts" setup>
import FlightSelectionPartial from './FlightSelectionPartial.vue'
import PassengerSectionPartial, { type Passenger } from './PassengerSectionPartial.vue'
import PaymentSectionPartial, { type PaymentInvoice } from './PaymentSectionPartial.vue'
import ObtainTicketPartial from './ObtainTicketPartial.vue'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import type { AirplaneTicket, IFlight } from '~/types'
import { useRotateKeyTransition } from '~/composables/useTransition'
import { usePaypal, useStripe } from '~/composables/usePayment'
import { axios } from '~/composables/general'

const props = defineProps<{ anchor?: HTMLElement }>()

const anchor = computed(() => props.anchor)
const hidden = ref(true)
const sector = reactive(useRotateKeyTransition(['flight', 'passenger', 'payment', 'obtain']))
const section = reactive<Partial<{
  flight: InstanceType<typeof FlightSelectionPartial>
  passenger: InstanceType<typeof PassengerSectionPartial>
  payment: InstanceType<typeof PaymentSectionPartial>
  obtain: InstanceType<typeof ObtainTicketPartial>
}>>({})

type Section = typeof section

const invoice = ref<PaymentInvoice>()
const passengers = ref(1)
const creating = ref(false)

const form = reactive<Partial<{
  search: string
  duration?: '7days'
  preferred?: { duration?: '7days' }
  flight: { departure: IFlight, returning?: IFlight }
  passengers: Passenger[]
  contact: { phone: string, email: string, prefix: string }
}>>({})

const bind = (key: keyof Section, ref: unknown) => section[key] = ref as any

const execute = <K extends keyof Section>(key: K, handler: (section: NonNullable<Section[K]>) => unknown) => {
  const instance = section[key]

  if (instance) {
    return handler(instance)
  }

  const unsubscribe = watch(() => section[key], () => {
    unsubscribe()
    nextTick(() => handler(section[key]!))
  })
}

type DisplayOpt = {
  mode: 'oneway'|'roundtrip',
  pax: number,
  depart: IFlight[]
  returning?: IFlight[]
  search: string,
  duration?: '7days'
}

const display = (opt: DisplayOpt) => {
  const { pax, depart, returning, mode, search, duration } = opt

  form.search = search
  form.duration = duration
  hidden.value = false
  sector.set('flight')
  passengers.value = pax

  execute('flight', (flight) => {
    flight.set(depart, mode === 'roundtrip' ? returning : undefined)

    nextTick(() => anchor.value?.scrollIntoView({ behavior: 'smooth' }))
  })
}

const obtain = async () => {
  sector.set('obtain')
}

const create = async () => {
  if (creating.value) {
    return
  }

  const { flight, passengers, contact, preferred } = form

  if (!contact || !passengers || !flight) {
    return
  }

  const returning = flight.returning
  creating.value = true

  return await axios.post<{ invoice: { _id: string }, ticket: AirplaneTicket }>('/public/v2/ticket/create', {
    preferred,
    search: form.search!,
    flights: [flight.departure.id, returning?.id].filter(Boolean),
    passengers: passengers.map(item => {
      return {
        prefix: item.prefix,
        firstName: item.first_name,
        familyName: item.family_name
      }
    }),
    contact: {
      email: contact.email,
      phone: {
        number: contact.phone,
        prefix: contact.prefix
      },
    },
  }, {
    headers: {
      // @ts-ignore require tracker.js from air.onwardfree.com
      'X-affiliate-tracker': window._onwardAffiliateTracker?.id
    }
  })
  .then(async ({ data }) => {
    invoice.value = await axios.get<PaymentInvoice>('/public/invoice/' + data.invoice._id)
      .then(res => res.data)

    sector.set('payment')
  })
  .catch(() => alert('Something went wrong, please try again'))
  .finally(() => creating.value = false)
}

defineExpose({ display })

watch(() => sector.current, () => {
  sector.is('payment') && nextTick(() => {
    anchor.value?.scrollIntoView({ behavior: 'smooth', })
  })
})

onMounted(() => {
  /**
   * Preload payment script
   */
  const unsubscribe = watch(() => sector.current, () => {
    if (sector.is('passenger')) {
      usePaypal()
      useStripe()
      unsubscribe()
    }
  })
})
</script>